import { AlertTriangle, User, Users } from 'react-feather';
import { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';
import omit from 'lodash/omit';

import { AddTeamMutation as AddTeam, GetTeamsQuery as GetTeams, Permission } from '__generated__/graphql';

import { ExportsContext } from '../../Exports/ExportsProvider';
import { assetUrl, isEmpty, isGraphQLValidationError } from '../../../common/helpers';
import AddTeamForm from './AddTeamForm';
import ExportTeamsForm from './ExportTeamsForm';
import GetTeamsQuery from './GetTeamsQuery';
import ProtectedSection from '../../Common/ProtectedSection';
import TeamFilters, { TeamFilterParams, TeamStats } from './TeamFilters';
import UI from '../../../common/UI';
import config from '../../config';
import useDocumentTitle from '../../../common/useDocumentTitle';
import useLocale from '../../../common/useLocale';
import useMediaDevice from '../../../common/useMediaDevice';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';
import useRecentEventId from '../../Common/useRecentEventId';
import useUrlState from '../../../common/useUrlState';

type Team = GetTeams['project']['teams']['data'][0];

interface UrlState extends TeamFilterParams {
  page?: number;
}

interface TeamCardProps {
  team: Team;
  showEvent?: boolean;
}

const TeamCard = ({ team, showEvent }: TeamCardProps) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const project = useProject();
  const to = `/${project.id}/participants/teams/${team.id}`;

  return (
    <UI.NavCard to={to}>
      <UI.GridContainer sc={{ alignVertical: 'center', columns: '3fr 2fr 2fr 1fr', gutter: 0.5 }}>
        <UI.Div style={{ minWidth: 0 }}>
          <UI.H4>
            <UI.Truncate>
              {team.title}
            </UI.Truncate>
          </UI.H4>
        </UI.Div>
        {showEvent && (
          <UI.FlexContainer sc={{ color: 'gray.500' }} style={{ minWidth: 0, gridColumn: '2 / span 2' }}>
            <UI.Label sc={{ small: true }} style={{ minWidth: 0 }}>
              <UI.Truncate>
                {team.event.title}
              </UI.Truncate>
            </UI.Label>
          </UI.FlexContainer>
        )}
        {!showEvent && (
          <>
            <UI.FlexContainer sc={{ color: 'gray.500' }} style={{ minWidth: 0 }}>
              <UI.Truncate>
                <UI.Icon>
                  <User />
                </UI.Icon>
                {' '}
                {team.captain.full_name}
              </UI.Truncate>
            </UI.FlexContainer>
            <UI.FlexContainer sc={{ color: 'gray.500' }} style={{ minWidth: 0 }}>
              {team.team_type.title && (
                <UI.Label sc={{ small: true }} style={{ minWidth: 0 }}>
                  <UI.Truncate>
                    {team.team_type.title}
                  </UI.Truncate>
                </UI.Label>
              )}
            </UI.FlexContainer>
          </>
        )}

        <UI.Div sc={{ color: 'gray.500' }}>
          <UI.TooltipContainer sc={{ inlineBlock: true, padding: 1, margin: -1 }}>
            <UI.Icon>
              <Users />
            </UI.Icon>
            {' '}
            {formatNumber(team.participants_count)}
            <UI.Tooltip sc={{ alignH: 'center' }}>
              {t('team_members')}
            </UI.Tooltip>
          </UI.TooltipContainer>
        </UI.Div>
      </UI.GridContainer>
    </UI.NavCard>
  );
};

const TeamsPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const device = useMediaDevice();
  const project = useProject();
  const recentEvent = useRecentEventId();

  useDocumentTitle(t('teams'));

  const [filtersKey, setFiltersKey] = useState(0);

  // Extract and sync url state to local state using useUrlState + useState => Initial state
  const [{ page, ...filters }, setUrlState] = useUrlState<UrlState>({
    parse: (rawParams, { string, integer }) => ({
      event: string(rawParams.event),
      search: string(rawParams.search),
      min_size: integer(rawParams.min_size),
      max_size: integer(rawParams.max_size),
      page: integer(rawParams.page, 1),
    }),
    onChange: () => {
      // Rerender the filters when user clicks on 'Teams' in the nav bar.
      setFiltersKey((key) => key + 1);
    },
  });

  // State that contains all filter stats
  const [filterStats, setFilterStats] = useState<TeamStats>();

  const teamsCount = filterStats?.teams || 0;
  const enableExport = teamsCount > 0 && teamsCount < config.maxExportSize.teams;

  /** Replaces filters in the URL */
  const handleFiltersChange = (update: (filters: TeamFilterParams) => TeamFilterParams) => {
    setUrlState(({ page, ...urlState }) => update({ ...urlState, ...filters }), true);
  };

  /** Filters object that can be sent to the server */
  const teamFilters = {
    ...omit(filters, 'event'),
    events: filters.event ? [filters.event] : undefined,
  };

  // Query (and filter) teams.
  const { data, loading } = useQuery(GetTeamsQuery, {
    variables: {
      ...teamFilters,
      project_id: project.id,
      page,
      per_page: 20,
    },
    onError: (error) => {
      if (isGraphQLValidationError(error)) {
        // Recent event no longer exists, reset it.
        recentEvent.reset();
      }
    },
  });

  const handleSuccess = (team: AddTeam['addTeam']) => {
    history.push(`/${project.id}/participants/teams/${team.id}`);
  };

  const [openAddTeamModal, closeAddTeamModal] = useModal(() => (
    <UI.Modal maxWidth={600} close={closeAddTeamModal}>
      <AddTeamForm
        eventId={filters.event}
        onSuccess={handleSuccess}
      />
    </UI.Modal>
  ), [filters.event]);

  const { openExportsModal } = useContext(ExportsContext);

  const [openExportTeamsModal, closeExportTeamsModal] = useModal(() => (
    <UI.Modal maxWidth={800} close={closeExportTeamsModal}>
      <ExportTeamsForm
        filters={teamFilters}
        teamsCount={teamsCount}
        onSuccess={() => { closeExportTeamsModal(); openExportsModal(); }}
      />
    </UI.Modal>
  ), [teamFilters, teamsCount]);

  const events = data?.project.events;
  const teams = data?.project.teams;

  const [showEvent, setShowEvent] = useState(!filters.event);
  const teamsRef = useRef(teams);

  // Show event labels after new teams have been retrieved.
  useEffect(() => {
    if (teamsRef.current !== teams) {
      setShowEvent(!filters.event);
    }
    teamsRef.current = teams;
  }, [teams, filters.event]);

  /**
   * Indicates whether a user has activated any filters.
   */
  const hasFilters = Object.values(filters).some((filter) => !isEmpty(filter));

  if (!events) {
    return <UI.PageLoader />;
  }

  return (
    <>
      {events.total > 0 && (
        <UI.Section>
          <UI.GridContainer>
            <UI.GridContainer sc={{ columns: '1fr 3fr' }}>
              <UI.SectionHeading>
                {t('teams')}
              </UI.SectionHeading>
              <UI.FlexContainer sc={{ justifyContent: 'end', spaceX: 1 }}>
                <ProtectedSection permissions={[Permission.edit_registrations]}>
                  <UI.Div sc={{ textAlign: 'right' }}>
                    <UI.Button sc={{ brand: 'secondary' }} onClick={openAddTeamModal}>
                      {t('add_team')}
                    </UI.Button>
                  </UI.Div>
                </ProtectedSection>
                {!device.isSmall && (
                  <ProtectedSection permissions={[Permission.exports]}>
                    <UI.TooltipContainer>
                      {teamsCount > config.maxExportSize.teams && (
                        <UI.Tooltip sc={{ alignH: 'center', alignV: 'bottom' }}>
                          <UI.Icon>
                            <AlertTriangle />
                          </UI.Icon>
                          {' '}
                          {t('export_too_large')}
                        </UI.Tooltip>
                      )}
                      <UI.Button
                        onClick={() => openExportTeamsModal()}
                        disabled={!enableExport}
                      >
                        {t('export')}
                      </UI.Button>
                    </UI.TooltipContainer>
                  </ProtectedSection>
                )}
              </UI.FlexContainer>
            </UI.GridContainer>

            <UI.GridContainer sc={{ columns: ['1fr', '1fr 3fr'] }}>
              <UI.Div>
                <TeamFilters
                  filters={filters}
                  onChange={handleFiltersChange}
                  onStatsChange={setFilterStats}
                  key={filtersKey}
                />
              </UI.Div>

              <UI.Div>
                <UI.GridContainer sc={{ gutter: 1 / 3, loading }}>
                  {teams.data.map((team) => (
                    <TeamCard team={team} showEvent={showEvent} key={team.id} />
                  ))}
                  {teams.total === 0 && (
                    <>
                      {hasFilters && (
                        <UI.Card
                          sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }}
                          style={{ textWrap: 'balance' }}
                        >
                          <UI.Div>
                            <img src={assetUrl('images/participant.svg')} alt="" height={120} />
                          </UI.Div>
                          {t('no_teams_match_filters')}
                          {' '}
                          <UI.Link to={`/${project.id}/participants/teams`}>{t('reset_filters')}</UI.Link>
                        </UI.Card>
                      )}

                      {!hasFilters && !loading && (
                        <UI.Card
                          sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }}
                          style={{ textWrap: 'balance' }}
                        >
                          <img src={assetUrl('images/participant.svg')} alt="" height={120} />
                          <UI.H3 sc={{ mb: 2 }}>{t('no_teams_yet')}</UI.H3>
                          <UI.Paragraph sc={{ mb: 0 }}>
                            {t('no_teams_get_started')}
                          </UI.Paragraph>
                        </UI.Card>
                      )}
                    </>
                  )}
                </UI.GridContainer>

                {teams && teams.last_page > 1 && (
                  <UI.Div sc={{ mt: 4 }}>
                    <UI.Pager pageCount={teams.last_page} />
                  </UI.Div>
                )}
              </UI.Div>
            </UI.GridContainer>
          </UI.GridContainer>
        </UI.Section>
      )}

      {events.total === 0 && (
        <UI.Section sc={{ compact: true, mt: { md: 8 } }}>
          <UI.Card sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }} style={{ textWrap: 'balance' }}>
            <img src={assetUrl('images/participant.svg')} alt="" height={120} />
            <UI.H3 sc={{ mb: 2 }}>{t('no_teams_yet')}</UI.H3>
            <UI.Paragraph>{t('no_teams_because_no_events')}</UI.Paragraph>
            <UI.NavButton to={`/${project.id}/events/add`} sc={{ brand: 'secondary' }}>
              {t('create_an_event')}
            </UI.NavButton>
          </UI.Card>
        </UI.Section>
      )}
    </>
  );
};

export default TeamsPage;
